// 192 -- start an empty event from a saved match day template. Picks a
// template + date; the server materialises the fields and a full grid of
// team-less matches in one transaction and sets the event window from the
// first/last slot. Only offered while the event has no matches.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { EventPublic, MatchDayTemplatePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { notifySaving, notifySaved } from "@/lib/save-status";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

export function ApplyTemplateDialog({ event, onApplied, onClose }: {
  event: EventPublic;
  onApplied: () => void;
  onClose: () => void;
}) {
  const [templates, setTemplates] = useState<MatchDayTemplatePublic[] | null>(null);
  const [selectedId, setSelectedId] = useState("");
  const [date, setDate] = useState(() => event.starts_at.slice(0, 10));
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.getMatchDayTemplates(event.school_id)
      .then((res) => {
        if (cancelled) return;
        setTemplates(res.templates);
        if (res.templates.length === 1) setSelectedId(res.templates[0].id);
      })
      .catch((e) => {
        if (cancelled) return;
        setTemplates([]);
        setError(e instanceof api.ApiError ? e.message : "Failed to load templates");
      });
    return () => { cancelled = true; };
  }, [event.school_id]);

  const selected = useMemo(
    () => templates?.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId],
  );

  const preview = useMemo(() => {
    if (!selected) return null;
    const times = selected.slots.map((s) => s.slot_starts_at_local).sort();
    const fields = new Set(selected.slots.map((s) => s.court_name).filter(Boolean));
    return {
      matches: selected.slots.length,
      fields: fields.size,
      first: times[0],
      last: times[times.length - 1],
    };
  }, [selected]);

  async function apply() {
    if (!selected || !date || applying) return;
    setApplying(true);
    setError(null);
    notifySaving();
    try {
      await api.applyTemplateToEvent(event.id, { template_id: selected.id, date });
      notifySaved();
      onApplied();
      onClose();
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to apply template");
      setApplying(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center overflow-y-auto bg-black/60 p-4 sm:items-center"
      data-dialog="apply-template"
    >
      <Card className="max-h-[90vh] w-full max-w-md overflow-y-auto">
        <CardHeader>
          <CardTitle>Start from template</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && <p className="text-sm text-destructive" data-error>{error}</p>}

          {templates === null ? (
            <p className="text-sm text-muted-foreground" data-state="loading">Loading templates...</p>
          ) : templates.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-state="empty">
              No templates saved yet. Build a schedule on any event and choose{" "}
              <span className="font-medium text-foreground">Save as template</span> to create one.
            </p>
          ) : (
            <>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Template</span>
                <select
                  title="Template"
                  data-field="apply-template-id"
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value)}
                  className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Pick a template...</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.slots.length} {t.slots.length === 1 ? "match" : "matches"})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Day</span>
                <input
                  type="date"
                  title="Day"
                  data-field="apply-template-date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>

              {preview && (
                <p className="text-sm text-muted-foreground" data-field="apply-preview">
                  Creates <span className="font-medium text-foreground">{preview.matches}</span>{" "}
                  {preview.matches === 1 ? "match" : "matches"}
                  {preview.fields > 0 && (
                    <>
                      {" "}across <span className="font-medium text-foreground">{preview.fields}</span>{" "}
                      {preview.fields === 1 ? "field" : "fields"}
                    </>
                  )}
                  , {preview.first} to {preview.last}. Drag teams on afterwards.
                </p>
              )}
            </>
          )}

          <div className="flex items-center justify-between gap-2">
            <Link
              to={`/schools/${event.school_id}/match-day-templates`}
              className="text-sm text-muted-foreground underline-offset-2 hover:underline"
              data-action="manage-templates"
            >
              Manage templates
            </Link>
            <div className="flex gap-2">
              <Button type="button" variant="outline" data-action="apply-template-cancel" onClick={onClose} disabled={applying}>
                Cancel
              </Button>
              <Button
                type="button"
                data-action="apply-template-confirm"
                onClick={apply}
                disabled={applying || !selected || !date}
              >
                {applying ? "Applying..." : "Apply template"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
